// Shared display formatters for live-ops labels — deltas, durations, clock
// times, and driver telemetry. Used by the Monitoring tab, composeDriverRecords,
// and the dashboard's refresh control. Every option below exists to preserve an
// exact production string — check the call sites before changing a default.

// '—' / 'on time' / '+7m' / '-3m'. unit 'min' renders '+7 min' (popup
// surfaces); zeroLabel null renders 0 as '0m' (marker label surfaces).
export function formatDeltaLabel(
  min,
  { unit = 'm', zeroLabel = 'on time', nullLabel = '—' } = {},
) {
  if (min === null || min === undefined) return nullLabel;
  const suffix = unit === 'min' ? ' min' : 'm';
  if (min === 0) return zeroLabel === null ? `0${suffix}` : zeroLabel;
  return min > 0 ? `+${min}${suffix}` : `${min}${suffix}`;
}

// Humanize a duration. Sub-hour stays in minutes ("45m"); over an hour
// switches to "1h" / "2h 30m" so labels stay readable at scale.
export function formatDurationMinutes(mins) {
  const abs = Math.abs(Math.round(mins));
  if (abs < 60) return `${abs}m`;
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

// What to call the place a driver stopped at. Fridge locations are named;
// every other location type stays generic. `FRIDGE` is the OpsLocations type
// literal used across the app (see SupervisorDashboard.js, DropOffSummary.js).
export function locationNoun(locationType) {
  return locationType === 'FRIDGE' ? 'fridge' : 'stop';
}

// How long a driver spent on site, naming where: "14m at fridge", else
// "14m at stop". One rule for every surface, so they can't drift apart.
export function formatOnSiteDuration(minutes, locationType) {
  return `${formatDurationMinutes(minutes)} at ${locationNoun(locationType)}`;
}

// `zone` suffixes the viewer's timezone ("6:10 AM CDT"): times render in
// the browser's zone and routes run across timezones, so data times say so.
// UI chrome ("Updated 3:04 PM") stays bare.
export function formatClockTime(
  date,
  { hour = 'numeric', seconds = false, zone = false } = {},
) {
  if (!date) return '';
  const options = { hour, minute: '2-digit' };
  if (seconds) options.second = '2-digit';
  if (zone) options.timeZoneName = 'short';
  return date.toLocaleTimeString([], options);
}

export function formatLastPingSeconds(seconds, { suffix = '' } = {}) {
  if (seconds === null || seconds === undefined) return null;
  const label = seconds < 60 ? `${seconds}s` : `${Math.round(seconds / 60)}m`;
  return `${label}${suffix}`;
}

export function formatBattery(level) {
  if (level === null || level === undefined) return null;
  return `${Math.round(level * 100)}%`;
}

export function pluralize(count, noun) {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

// "sarah.johnson@farmersfridge.com" → "Sarah Johnson". Empty string for a
// missing email so callers can render the result directly.
export function formatDriverName(email) {
  if (!email) return '';
  return email
    .split('@')[0]
    .replace(/[._]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// Everyone who worked a route, as one label ("Curtis Jensen, Theresa
// Montejano"). Routes are sometimes shared, so route-level surfaces list all
// drivers rather than guessing a single owner.
export function formatDriverNames(names, fallback = 'No driver') {
  return Array.isArray(names) && names.length > 0 ? names.join(', ') : fallback;
}

// "3.2 mi" one-decimal distance label. Null-safe.
export function formatMiles(miles) {
  if (miles === null || miles === undefined) return null;
  return `${Number(miles).toFixed(1)} mi`;
}
